"use client";
import { useState, useEffect, useRef } from "react";
import { useInView } from "@/hooks/useInView";
import { motion, AnimatePresence } from "motion/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AspectRatio } from "../ui/aspect-ratio";
import { Reveal } from "../reveal";
import { useAppSelector } from "@/store/hooks";
import { ExtractBySectionType, SectionType } from "types";
import { PortableText } from "next-sanity";
import { components } from "../shared/portable-text";
import { SanityImage } from "../sanity-image";

type TabsSectionType = ExtractBySectionType<SectionType, "tabs", "_type">;

export function Solutions({ data }: { data: TabsSectionType }) {
  const { heading, tabItems, description } = data;
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(true);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [resetTimer, setResetTimer] = useState(0);
  const [tabsListRef, isInView] = useInView<HTMLDivElement>();
  const { isOverlayOpen } = useAppSelector((state) => state.overlay);
  const [isPaused, setIsPaused] = useState(false);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoAdvanceEnabled && isInView && !isOverlayOpen && !isPaused) {
      interval = setInterval(() => {
        setCurrentTabIndex((prev) => (prev + 1) % tabItems.length);
      }, 3500);
    }

    return () => clearInterval(interval);
  }, [
    autoAdvanceEnabled,
    resetTimer,
    isInView,
    isOverlayOpen,
    isPaused,
    tabItems.length,
  ]);

  return (
    <section className="max-w-lg md:max-w-4xl lg:max-w-6xl xl:max-w-screen-2xl mx-auto py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-white">
      <Reveal>
        <h3 className="font-medium text-lg sm:text-xl md:text-2xl lg:text-3xl">
          {heading}
        </h3>
        {description && (
          <PortableText
            value={description}
            components={{
              block: {
                normal: ({ children }) => (
                  <p className="max-w-screen-sm mt-2 mb-8 md:mb-12 lg:mb-16 text-xs sm:text-base md:text-lg lg:text-xl">
                    {children}
                  </p>
                ),
              },
            }}
          />
        )}
      </Reveal>
      <Reveal initialY={"10%"}>
        <Tabs
          defaultValue={tabItems[0].name}
          value={tabItems[currentTabIndex].name}
          className="flex flex-col md:flex-row xl:flex-row gap-4 md:gap-12 lg:gap-16 xl:gap-24 justify-center"
        >
          <TabsList
            className="flex md:flex-col xl:flex-col flex-row h-full bg-white items-start gap-3 md:gap-4 overflow-x-auto md:overflow-x-visible"
            ref={tabsListRef}
          >
            {tabItems.map((solution, index) => (
              <TabsTrigger value={solution.name} key={solution.name} asChild>
                <button
                  onClick={() => {
                    setCurrentTabIndex(index);
                    setAutoAdvanceEnabled(false);

                    setTimeout(() => {
                      setAutoAdvanceEnabled(true);
                      setResetTimer((prev) => prev + 1);
                    }, 10000);
                  }}
                  className="relative py-2 px-3 md:px-4 text-xs md:text-sm lg:text-base gap-2 cursor-pointer data-[state=active]:text-secondary-500 group data-[state=active]:bg-transparent data-[state=active]:shadow-none transition duration-500 whitespace-nowrap md:whitespace-normal"
                >
                  {currentTabIndex === index && (
                    <motion.div
                      layoutId="solution"
                      className={`absolute h-full w-full group-data-[state=active]:bg-secondary-50 group-data-[state=active]:shadow-sm group-data-[state=active]:rounded-full group-data-[state=active]:hover:bg-secondary-100/80`}
                    />
                  )}
                  <span className="relative">
                    {solution.button.textBeforeIcon}
                    {solution.button.textAfterIcon}
                  </span>
                </button>
              </TabsTrigger>
            ))}
          </TabsList>

          <div
            ref={contentContainerRef}
            className="flex flex-col h-full flex-1"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
          >
            {tabItems.map((solution, index) => (
              <TabsContent
                value={solution.name}
                key={solution.name}
                className="flex-grow" // Place all in same grid cell
                style={{
                  opacity: currentTabIndex === index ? 1 : 0,
                  pointerEvents: currentTabIndex === index ? "auto" : "none",
                  transition: "opacity 0.3s ease",
                }}
              >
                {/* Your existing content */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={solution.name}
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -30, opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex flex-col md:flex-col lg:flex-row gap-4 sm:gap-6 md:gap-8 lg:gap-12 xl:gap-24"
                  >
                    <div className="w-full shrink-0 md:hidden xl:block xl:w-[450px] mx-auto md:mx-auto lg:mx-0">
                      <AspectRatio ratio={442 / 410}>
                        <SanityImage
                          src={solution.image}
                          alt="event"
                          fill
                          placeholder="blur"
                          blurDataURL={
                            solution.image.asset?.metadata?.lqip || ""
                          }
                          className="object-cover rounded-2xl cursor-pointer"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        />
                      </AspectRatio>
                    </div>

                    <div className="w-full solution shrink-1">
                      <PortableText
                        value={solution.content}
                        components={components}
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </Reveal>
    </section>
  );
}
